import { createContext, useContext, useState, useEffect, useCallback, PropsWithChildren } from 'react';
import { useApi, fetchApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { QUAY_CONTEXT_REFRESH_INTERVAL_MS } from '../../polling-config';

export type WatchedRepo = { namespace: string; repoName: string };
export type Repo = {
  namespace: string;
  name: string;
  description: string;
  is_public: boolean;
  last_modified?: number;
  tags?: any[];
  tagsLoaded?: boolean;
  expanded?: boolean;
};

interface QuayContextValue {
  repos: Repo[];
  orgs: string[];
  loading: boolean;
  error: string;
  watchedRepos: WatchedRepo[];
  refresh: () => void;
  toggleWatch: (namespace: string, repoName: string) => Promise<void>;
  updateRepo: (namespace: string, name: string, patch: Partial<Repo>) => void;
}

const QuayContext = createContext<QuayContextValue | null>(null);

export const QuayProvider = ({ children }: PropsWithChildren<{}>) => {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [repos, setRepos] = useState<Repo[]>([]);
  const [orgs, setOrgs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [watchedRepos, setWatchedRepos] = useState<WatchedRepo[]>([]);

  const proxyBase = useCallback(async () => {
    const base = await discoveryApi.getBaseUrl('portal');
    return `${base}/quay`;
  }, [discoveryApi]);

  const authBase = useCallback(async () => {
    return await discoveryApi.getBaseUrl('portal');
  }, [discoveryApi]);

  const loadWatched = useCallback(async () => {
    try {
      const base = await authBase();
      const resp = await fetchApi.fetch(`${base}/quay-watched`);
      if (resp.ok) setWatchedRepos(await resp.json());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load watched repos');
    }
  }, [fetchApi, authBase]);

  const loadRepos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const base = await proxyBase();
      let nextUrl: string | null = `${base}/api/v1/repository?public=true&limit=100`;
      const all: Repo[] = [];
      const orgSet = new Set<string>();

      while (nextUrl) {
        const resp: Response = await fetchApi.fetch(nextUrl);
        if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
        const data: any = await resp.json();
        for (const r of (data.repositories ?? [])) {
          all.push({
            namespace: r.namespace,
            name: r.name,
            description: r.description ?? '',
            is_public: r.is_public ?? true,
            last_modified: r.last_modified,
          });
          orgSet.add(r.namespace);
        }
        nextUrl = data.next_page
          ? `${base}/api/v1/repository?public=true&limit=100&next_page=${encodeURIComponent(data.next_page)}`
          : null;
      }

      all.sort((a, b) => (b.last_modified ?? 0) - (a.last_modified ?? 0));
      setRepos(all);
      setOrgs(Array.from(orgSet).sort());
    } catch (e: any) {
      setError(`Failed to load repositories: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [fetchApi, proxyBase]);

  // Fire on app start + background refresh
  useEffect(() => {
    loadRepos();
    loadWatched();
    if (QUAY_CONTEXT_REFRESH_INTERVAL_MS > 0) {
      const id = setInterval(() => { loadRepos(); loadWatched(); }, QUAY_CONTEXT_REFRESH_INTERVAL_MS);
      return () => clearInterval(id);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleWatch = useCallback(async (namespace: string, repoName: string) => {
    const isWatched = watchedRepos.some(w => w.namespace === namespace && w.repoName === repoName);
    try {
      const base = await authBase();
      let resp: Response;
      if (isWatched) {
        resp = await fetchApi.fetch(
          `${base}/quay-watched/${encodeURIComponent(namespace)}/${encodeURIComponent(repoName)}`,
          { method: 'DELETE' },
        );
      } else {
        resp = await fetchApi.fetch(`${base}/quay-watched`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ namespace, repoName }),
        });
      }
      if (resp.ok) setWatchedRepos(await resp.json());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to toggle watch');
    }
  }, [fetchApi, authBase, watchedRepos]);

  const updateRepo = useCallback((namespace: string, name: string, patch: Partial<Repo>) => {
    setRepos(prev => prev.map(r =>
      r.namespace === namespace && r.name === name ? { ...r, ...patch } : r
    ));
  }, []);

  return (
    <QuayContext.Provider value={{ repos, orgs, loading, error, watchedRepos, refresh: loadRepos, toggleWatch, updateRepo }}>
      {children}
    </QuayContext.Provider>
  );
};

export const useQuayData = (): QuayContextValue => {
  const ctx = useContext(QuayContext);
  if (!ctx) throw new Error('useQuayData must be used within QuayProvider');
  return ctx;
};
